export const DURATION_FRAMES = 196;

import React from "react";
import { AbsoluteFill, Audio, Sequence, staticFile, useCurrentFrame, interpolate, spring, useVideoConfig } from "remotion";
import { noise2D } from "@remotion/noise";
import { dark, fonts, safeZone, gradients } from "../styles/theme";

/*
 * Reel_cmohpdbwx00003ps2po9uk1qt
 * [TEST] Reel de prueba
 * 196 frames @ 30fps
 * Scene timings:
 *   - Scene 1 "Test": from=0, duration=196
 */

const TOTAL_FRAMES = 196;

const FloatingParticles: React.FC = () => {
  const frame = useCurrentFrame();
  const particles = Array.from({ length: 16 }, (_, i) => i);
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      {particles.map((i) => {
        const nx = noise2D("particle-x-" + i, frame * 0.008, 0);
        const ny = noise2D("particle-y-" + i, frame * 0.008, 0);
        const x = 540 + nx * 520;
        const y = 960 + ny * 920;
        const hue = 200 + noise2D("particle-hue-" + i, frame * 0.004, 0) * 40;
        const size = 4 + ((i % 4) * 2);
        const op = 0.06 + ((i % 3) * 0.015);
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: x,
              top: y,
              width: size,
              height: size,
              borderRadius: "50%",
              background: `hsl(${hue}, 90%, 70%)`,
              opacity: op + 0.03,
              filter: "blur(1px)",
              boxShadow: `0 0 12px hsl(${hue}, 90%, 70%)`,
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};

const LightSweep: React.FC = () => {
  const frame = useCurrentFrame();
  const left = interpolate(frame, [0, TOTAL_FRAMES], [-300, 1280], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const op = interpolate(
    frame,
    [0, 30, TOTAL_FRAMES - 30, TOTAL_FRAMES],
    [0, 0.18, 0.18, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );
  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left,
        width: 220,
        height: "100%",
        background:
          "linear-gradient(90deg, transparent, rgba(96,165,250,0.22), transparent)",
        transform: "skewX(-12deg)",
        opacity: op,
        pointerEvents: "none",
        mixBlendMode: "screen",
      }}
    />
  );
};

const VignetteOverlay: React.FC = () => {
  const frame = useCurrentFrame();
  const op = interpolate(
    frame,
    [0, 40, TOTAL_FRAMES - 40, TOTAL_FRAMES],
    [0.6, 0.45, 0.45, 0.6],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );
  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,${op}) 100%)`,
        pointerEvents: "none",
      }}
    />
  );
};

const BlueGlow: React.FC = () => {
  const frame = useCurrentFrame();
  const op = interpolate(
    frame,
    [0, 40, TOTAL_FRAMES - 30, TOTAL_FRAMES],
    [0, 1, 1, 0.4],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );
  return (
    <AbsoluteFill
      style={{
        background: gradients.darkGlow,
        opacity: op,
        pointerEvents: "none",
      }}
    />
  );
};

const RisingBokeh: React.FC = () => {
  const frame = useCurrentFrame();
  const dots = Array.from({ length: 10 }, (_, i) => i);
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      {dots.map((i) => {
        const seed = i * 37;
        const x = 80 + ((seed * 53) % 920);
        const baseY = 1700 + ((seed * 17) % 200);
        const speed = 0.6 + ((i % 4) * 0.18);
        const y = baseY - frame * speed;
        const op = interpolate(
          frame,
          [0, 30, TOTAL_FRAMES - 30, TOTAL_FRAMES],
          [0, 0.35, 0.35, 0],
          { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
        );
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: x,
              top: y,
              width: 8 + (i % 3) * 4,
              height: 8 + (i % 3) * 4,
              borderRadius: "50%",
              background: "rgba(96,165,250,0.6)",
              boxShadow: "0 0 18px rgba(96,165,250,0.7)",
              opacity: op,
              filter: "blur(0.5px)",
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};

const ContinuousBackground: React.FC = () => {
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <BlueGlow />
      <FloatingParticles />
      <RisingBokeh />
      <LightSweep />
      <VignetteOverlay />
    </AbsoluteFill>
  );
};

const CoreDigitalLogo: React.FC<{ opacity: number }> = ({ opacity }) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        opacity,
      }}
    >
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <defs>
          <linearGradient id="logoGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#60a5fa" />
            <stop offset="100%" stopColor="#2563eb" />
          </linearGradient>
        </defs>
        <circle
          cx="28"
          cy="28"
          r="22"
          stroke="url(#logoGrad)"
          strokeWidth="3"
          fill="none"
        />
        <circle cx="28" cy="28" r="9" fill="url(#logoGrad)" />
        <circle
          cx="28"
          cy="28"
          r="14"
          stroke="rgba(96,165,250,0.4)"
          strokeWidth="1.5"
          fill="none"
        />
      </svg>
      <div
        style={{
          color: dark.text,
          fontFamily: fonts.headline,
          fontWeight: 700,
          fontSize: 26,
          letterSpacing: 1.2,
        }}
      >
        CORE<span style={{ color: dark.accentBlueMuted }}> DIGITAL</span>
      </div>
    </div>
  );
};

const SceneTest: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const cardSpring = spring({
    frame,
    fps,
    config: { damping: 20, stiffness: 80, mass: 1 },
  });
  const cardTranslate = interpolate(cardSpring, [0, 1], [60, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const cardOpacity = interpolate(frame, [0, 25], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const titleOpacity = interpolate(frame, [12, 38], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const titleY = interpolate(frame, [12, 38], [40, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const labelOpacity = interpolate(frame, [30, 55], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const logoOpacity = interpolate(frame, [50, 80], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const progress = interpolate(
    frame,
    [40, TOTAL_FRAMES - 10],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  const exitOpacity = interpolate(
    frame,
    [TOTAL_FRAMES - 18, TOTAL_FRAMES],
    [1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  const pulse =
    1 + Math.sin(frame * 0.08) * 0.015;

  return (
    <AbsoluteFill
      style={{
        paddingTop: safeZone.top,
        paddingBottom: safeZone.bottom,
        paddingLeft: safeZone.horizontal,
        paddingRight: safeZone.horizontal,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        opacity: exitOpacity,
      }}
    >
      <div
        style={{
          opacity: labelOpacity,
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 22px",
          borderRadius: 999,
          background: dark.surface,
          border: `1px solid ${dark.border}`,
          boxShadow: dark.glowBlue,
        }}
      >
        <div
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: dark.accentBlueMuted,
            boxShadow: "0 0 12px rgba(96,165,250,0.8)",
          }}
        />
        <div
          style={{
            color: dark.textSecondary,
            fontFamily: fonts.body,
            fontWeight: 600,
            fontSize: 22,
            letterSpacing: 2,
            textTransform: "uppercase",
          }}
        >
          Pipeline Test
        </div>
      </div>

      <div
        style={{
          width: "100%",
          opacity: cardOpacity,
          transform: `translateY(${cardTranslate}px) scale(${pulse})`,
          background: "rgba(255,255,255,0.05)",
          borderRadius: 28,
          border: "1px solid rgba(255,255,255,0.10)",
          boxShadow: dark.glowBlueStrong,
          padding: "72px 48px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 36,
          backdropFilter: "blur(18px)",
        }}
      >
        <div
          style={{
            opacity: titleOpacity,
            transform: `translateY(${titleY}px)`,
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontFamily: fonts.body,
              fontSize: 28,
              fontWeight: 600,
              color: dark.textTertiary,
              letterSpacing: 4,
              textTransform: "uppercase",
              marginBottom: 28,
            }}
          >
            Scene 01
          </div>
          <div
            style={{
              fontFamily: fonts.headline,
              fontWeight: 800,
              fontSize: 92,
              lineHeight: 1.05,
              color: dark.text,
              textAlign: "center",
              letterSpacing: -1.5,
            }}
          >
            Esto es un{" "}
            <span
              style={{
                background:
                  "linear-gradient(135deg, #60a5fa, #2563eb, #818cf8)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              test
            </span>
          </div>
        </div>

        <div
          style={{
            width: "100%",
            maxWidth: 620,
            opacity: labelOpacity,
          }}
        >
          <div
            style={{
              height: 6,
              width: "100%",
              borderRadius: 999,
              background: "rgba(255,255,255,0.08)",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                width: `${progress * 100}%`,
                background:
                  "linear-gradient(90deg, #60a5fa, #2563eb, #818cf8)",
                boxShadow: "0 0 16px rgba(96,165,250,0.6)",
                borderRadius: 999,
              }}
            />
          </div>
          <div
            style={{
              marginTop: 16,
              display: "flex",
              justifyContent: "space-between",
              fontFamily: fonts.body,
              fontSize: 20,
              color: dark.textSecondary,
              fontWeight: 600,
              letterSpacing: 1.5,
              textTransform: "uppercase",
            }}
          >
            <span>Generación</span>
            <span style={{ color: dark.accentBlueMuted }}>
              {Math.round(progress * 100)}%
            </span>
          </div>
        </div>
      </div>

      <CoreDigitalLogo opacity={logoOpacity} />
    </AbsoluteFill>
  );
};

const Reel_cmohpdbwx00003ps2po9uk1qt: React.FC = () => {
  return (
    <AbsoluteFill style={{ background: dark.bg }}>
      <ContinuousBackground />
      <Sequence from={5}>
        <Audio src={staticFile("audio/cmohpdbwx00003ps2po9uk1qt.mp3")} />
      </Sequence>
      <Sequence from={0} durationInFrames={196}>
        <SceneTest />
      </Sequence>
    </AbsoluteFill>
  );
};

export default Reel_cmohpdbwx00003ps2po9uk1qt;
;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
